/* Feature Highlights — expanding card carousel (sections/ADD-feature-highlights.liquid)
   One card is always active. Click/tap a card, drag/swipe the track, use the arrows,
   or ←/→/Home/End on a focused card to change which card is expanded. */
if (!customElements.get('feature-highlights')) {
  class FeatureHighlights extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.feat-hl__track');
      this.cards = Array.from(this.querySelectorAll('.feat-hl__card'));
      if (!this.track || !this.cards.length) return;

      const section = this.closest('.feat-hl');
      this.arrows = section ? Array.from(section.querySelectorAll('.feat-hl__arrow')) : [];
      this.counter = section ? section.querySelector('[data-current]') : null;

      this.index = Math.max(0, this.cards.findIndex((c) => c.classList.contains('is-active')));
      this.track.style.setProperty('--fh-n', this.cards.length);
      this.drag = null;

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onArrow = this.onArrow.bind(this);

      this.addEventListener('pointerdown', this.onPointerDown);
      this.addEventListener('pointermove', this.onPointerMove);
      this.addEventListener('pointerup', this.onPointerUp);
      this.addEventListener('pointercancel', this.onPointerUp);
      this.addEventListener('keydown', this.onKeyDown);
      this.addEventListener('dragstart', (e) => e.preventDefault());
      this.arrows.forEach((btn) => btn.addEventListener('click', this.onArrow));

      this.goTo(this.index, false);
    }

    disconnectedCallback() {
      this.arrows.forEach((btn) => btn.removeEventListener('click', this.onArrow));
    }

    goTo(i, focus) {
      const next = Math.min(Math.max(i, 0), this.cards.length - 1);
      this.index = next;
      this.cards.forEach((card, n) => {
        const active = n === next;
        card.classList.toggle('is-active', active);
        card.setAttribute('aria-expanded', active ? 'true' : 'false');
        card.tabIndex = active ? 0 : -1;
      });
      this.track.style.setProperty('--fh-i', next);
      if (focus) this.cards[next].focus({ preventScroll: true });

      if (this.counter) this.counter.textContent = next + 1;
      this.arrows.forEach((btn) => {
        const dir = Number(btn.dataset.dir);
        btn.disabled = (dir < 0 && next === 0) || (dir > 0 && next === this.cards.length - 1);
      });
    }

    // Distance between the left edges of two collapsed cards
    stepSize() {
      const idle = this.cards.find((c) => !c.classList.contains('is-active')) || this.cards[0];
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return idle.getBoundingClientRect().width + gap;
    }

    onPointerDown(e) {
      if (e.button !== 0) return;
      this.drag = {
        id: e.pointerId,
        x: e.clientX,
        y: e.clientY,
        dx: 0,
        moving: false,
        card: e.target.closest('.feat-hl__card'),
      };
    }

    onPointerMove(e) {
      const d = this.drag;
      if (!d || e.pointerId !== d.id) return;
      const dx = e.clientX - d.x;
      const dy = e.clientY - d.y;

      if (!d.moving) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        // Vertical intent: let the page scroll
        if (Math.abs(dy) > Math.abs(dx)) {
          this.drag = null;
          return;
        }
        d.moving = true;
        this.classList.add('is-dragging');
        try { this.setPointerCapture(e.pointerId); } catch (err) {}
      }

      // Resist dragging past either end
      const atStart = this.index === 0 && dx > 0;
      const atEnd = this.index === this.cards.length - 1 && dx < 0;
      d.dx = atStart || atEnd ? dx * 0.3 : dx;
      this.track.style.setProperty('--fh-drag', d.dx + 'px');
    }

    onPointerUp(e) {
      const d = this.drag;
      if (!d || e.pointerId !== d.id) return;
      this.drag = null;

      if (!d.moving) {
        // A tap/click anywhere on a card focuses it
        if (e.type === 'pointerup' && d.card) this.goTo(this.cards.indexOf(d.card), false);
        return;
      }

      this.classList.remove('is-dragging');
      this.track.style.setProperty('--fh-drag', '0px');
      try { this.releasePointerCapture(e.pointerId); } catch (err) {}

      const step = this.stepSize();
      let moved = Math.round(-d.dx / step);
      if (moved === 0 && Math.abs(d.dx) > 40) moved = d.dx < 0 ? 1 : -1;
      if (moved) this.goTo(this.index + moved, false);
    }

    onKeyDown(e) {
      const keys = { ArrowRight: 1, ArrowLeft: -1 };
      if (e.key in keys) {
        e.preventDefault();
        this.goTo(this.index + keys[e.key], true);
      } else if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault();
        this.goTo(e.key === 'Home' ? 0 : this.cards.length - 1, true);
      } else if ((e.key === 'Enter' || e.key === ' ') && e.target.classList.contains('feat-hl__card')) {
        e.preventDefault();
        this.goTo(this.cards.indexOf(e.target), true);
      }
    }

    onArrow(e) {
      this.goTo(this.index + Number(e.currentTarget.dataset.dir), false);
    }
  }

  customElements.define('feature-highlights', FeatureHighlights);
}
